import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import useSessionTimeout from '@/hooks/useSessionTimeout';
import SessionTimeoutModal from './SessionTimeoutModal';

const SessionTimeoutProvider = ({ children }) => {
  const { signOut, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [showModal, setShowModal] = useState(false);
  const signOutTimerRef = useRef(null);
  const startSessionTimerRef = useRef(null);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      if (signOutTimerRef.current) clearTimeout(signOutTimerRef.current);
      if (startSessionTimerRef.current) clearTimeout(startSessionTimerRef.current);
    };
  }, []);

  // Handle session timeout callbacks
  const handleTimeout = () => {
    toast({
      title: 'Session Expired',
      description: 'You have been signed out due to inactivity.',
      variant: 'error'
    });

    // Sign out after a short delay
    signOutTimerRef.current = setTimeout(() => {
      signOut();
    }, 2000);
  };

  const handleWarning = (minutesLeft) => {
    toast({
      title: 'Session Timeout Warning',
      description: `Your session will expire in ${minutesLeft} minutes due to inactivity.`,
      variant: 'warning',
      duration: 10000 // Show for 10 seconds
    });
    setShowModal(true);
  };

  // Initialize session timeout hook
  const sessionTimeout = useSessionTimeout(
    30, // Always 30 minutes, but will be controlled by isAuthenticated
    handleTimeout, 
    handleWarning
  );

  // Start session timeout when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Start the session timeout with a delay to ensure auth is complete and user has time to interact
      startSessionTimerRef.current = setTimeout(() => {
        sessionTimeout.startSession();
      }, 5000); // 5 seconds delay
    } else {
      // Reset when user logs out
      sessionTimeout.resetTimer();
    }
  }, [isAuthenticated, sessionTimeout]);

  const handleExtendSession = () => {
    sessionTimeout.extendSession();
    setShowModal(false);
    
    toast({
      title: 'Session Extended',
      description: 'Your session has been extended.',
      variant: 'success'
    });
  };

  const handleSignOut = () => {
    setShowModal(false);
    signOut();
  };

  return (
    <>
      {children}
      <SessionTimeoutModal
        isOpen={showModal}
        timeRemaining={sessionTimeout.formatTimeRemaining()}
        onExtendSession={handleExtendSession}
        onClose={handleSignOut}
      />
    </>
  );
};

export default SessionTimeoutProvider;
